// Thin typed wrapper over window.Telegram.WebApp. The rest of the codebase
// should go through here rather than reaching into window directly, so the
// "are we actually inside Telegram?" decision lives in one place.

export type HapticStyle = 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'
export type NotificationType = 'error' | 'success' | 'warning'

export interface TelegramPopupButton {
  id?: string
  type?: 'default' | 'ok' | 'close' | 'cancel' | 'destructive'
  text?: string
}

export interface TelegramThemeParams {
  bg_color?: string
  text_color?: string
  hint_color?: string
  link_color?: string
  button_color?: string
  button_text_color?: string
  secondary_bg_color?: string
  section_separator_color?: string
  destructive_text_color?: string
  header_bg_color?: string
}

interface TelegramBackButton {
  isVisible: boolean
  show(): void
  hide(): void
  onClick(cb: () => void): void
  offClick(cb: () => void): void
}

interface TelegramMainButton {
  text: string
  isVisible: boolean
  setText(text: string): void
  show(): void
  hide(): void
  onClick(cb: () => void): void
  offClick(cb: () => void): void
  enable(): void
  disable(): void
}

interface TelegramHapticFeedback {
  impactOccurred(style: HapticStyle): void
  notificationOccurred(type: NotificationType): void
  selectionChanged(): void
}

interface TelegramWebApp {
  initData: string
  initDataUnsafe: Record<string, unknown>
  version: string
  platform: string
  colorScheme: 'light' | 'dark'
  themeParams: TelegramThemeParams
  isExpanded: boolean
  viewportHeight: number
  viewportStableHeight: number
  BackButton: TelegramBackButton
  MainButton: TelegramMainButton
  HapticFeedback: TelegramHapticFeedback
  ready(): void
  expand(): void
  close(): void
  openLink(url: string, options?: { try_instant_view?: boolean }): void
  openTelegramLink(url: string): void
  showPopup(
    params: { title?: string; message: string; buttons?: TelegramPopupButton[] },
    cb?: (buttonId: string) => void,
  ): void
  showAlert(message: string, cb?: () => void): void
  showConfirm(message: string, cb?: (confirmed: boolean) => void): void
  onEvent(type: string, cb: (...args: unknown[]) => void): void
  offEvent(type: string, cb: (...args: unknown[]) => void): void
  setHeaderColor?(color: string): void
  setBackgroundColor?(color: string): void
  // Bot API 7.7+, so optional: older clients simply do not have them.
  disableVerticalSwipes?(): void
  enableVerticalSwipes?(): void
  isVerticalSwipesEnabled?: boolean
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp }
  }
}

export function getTelegramWebApp(): TelegramWebApp | null {
  if (typeof window === 'undefined') return null
  return window.Telegram?.WebApp ?? null
}

// `platform === 'unknown'` is what Telegram reports when the SDK script is
// loaded but we're NOT running inside a Telegram WebView. Anything else —
// 'ios', 'android', 'tdesktop', 'web', etc. — means real Telegram.
export function isInTelegram(): boolean {
  const wa = getTelegramWebApp()
  return !!wa && wa.platform !== 'unknown'
}

function applyThemeParams(wa: TelegramWebApp) {
  const root = document.documentElement
  const params = wa.themeParams ?? {}
  const set = (name: string, value: string | undefined) => {
    if (!value) return
    root.style.setProperty(name, value)
  }
  set('--tg-theme-bg-color', params.bg_color)
  set('--tg-theme-text-color', params.text_color)
  set('--tg-theme-hint-color', params.hint_color)
  set('--tg-theme-link-color', params.link_color)
  set('--tg-theme-button-color', params.button_color)
  set('--tg-theme-button-text-color', params.button_text_color)
  set('--tg-theme-secondary-bg-color', params.secondary_bg_color)
  set('--tg-theme-section-separator-color', params.section_separator_color)
  set('--tg-theme-destructive-text-color', params.destructive_text_color)

  // Reflect colorScheme so Radix Theme can pick it up via data attribute.
  root.dataset.tgTheme = wa.colorScheme
  root.dataset.tgPlatform = wa.platform
}

// Call once on runtime startup. Idempotent.
export function initTelegramIntegration(): void {
  const wa = getTelegramWebApp()
  if (!wa) return

  try {
    wa.ready()
    wa.expand()
  } catch {
    /* SDK may throw outside Telegram — safe to ignore */
  }

  // Telegram closes the app on a downward swipe. In a terminal that gesture means
  // "scroll up through the output", so the app would vanish mid-read. This turns off
  // the dismiss gesture only — scrolling inside the app still works, and the header
  // chevron still closes it. Bot API 7.7+; older clients keep the old behaviour.
  try {
    wa.disableVerticalSwipes?.()
  } catch {
    /* ignore */
  }

  applyThemeParams(wa)

  // Re-apply on theme changes (user flips Telegram theme mid-session).
  try {
    wa.onEvent('themeChanged', () => applyThemeParams(wa))
  } catch {
    /* ignore */
  }
}
